import { darken } from 'polished';
import React, { FC } from 'react';
import { Rect } from '../../../platform/geom/Rect';
import { RenderBounds, RenderBox, RenderInner, RenderSvg, usePose, usePressable2 } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { Stopwatch, StopwatchReadingAttr } from './Stopwatch';
import { StopwatchFieldTextSettings, StopwatchMinutesFieldTextSettings, StopwatchSecondsFieldTextSettings } from './StopwatchFields';

const coalesceReading = (object: Stopwatch) => {
    return StopwatchReadingAttr.get(object) ?? [
        `${parseInt(object.minutes) || 0}`.padStart(2, '0'),
        `${parseInt(object.seconds) || 0}`.padStart(2, '0'),
    ];
};

const shadeColor = (color: string, state: string) =>
    color = (
        (state === 'active' ? darken(.3, color) : null) ??
        (state === 'hover' ? darken(.2, color) : null) ??
        darken(.15, color)
    );

const StartButton = React.memo<{ area: Rect, state: string, color: string }>(({ area, state, color }) => {
    color = shadeColor(color, state);
    return <RenderSvg area={area}>
        <rect width="40" height="40" rx="2" fill={color} />
        <path d="M30.5 19.134C31.1667 19.5189 31.1667 20.4811 30.5 20.866L15.5 29.5263C14.8333 29.9112 14 29.4301 14 28.6603L14 11.3397C14 10.5699 14.8333 10.0888 15.5 10.4737L30.5 19.134Z" fill="#F9FAFC" />
    </RenderSvg>;
});

const StopButton = React.memo<{ area: Rect, state: string, color: string }>(({ area, state, color }) => {
    color = shadeColor(color, state);
    return <RenderSvg area={area}>
        <rect width="40" height="40" rx="2" fill={color} />
        <rect x="10" y="10" width="20" height="20" rx="2" fill="white" />
    </RenderSvg>;
});

const AddTimeButton = React.memo<{ area: Rect, state: string, color: string }>(({ area, state, color }) => {
    color = shadeColor(color, state);
    return <RenderSvg area={area}>
        <rect width="40" height="40" rx="2" fill={color} />
        <rect x="18" y="10" width="4" height="20" rx="2" fill="#F9FAFC" />
        <rect x="30" y="18" width="4" height="20" rx="2" transform="rotate(90 30 18)" fill="#F9FAFC" />
    </RenderSvg>;
});

export const StopwatchVisual: FC<{ object: Stopwatch }> = ({ object }) => {
    const { screen } = object.layout;
    const { beingEdited } = usePose(object);

    const startStop = usePressable2(object, 'startStop');
    const addTime = usePressable2(object, 'addTime');
    const [mins, secs] = coalesceReading(object);

    const playing = !!object.timeStarted;

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderInner object={object}>
                    <RenderBox inset="0" background={object.color} borderRadius="12px">
                        {!playing && <StartButton color={object.color} {...startStop} />}
                        {playing && <StopButton color={object.color} {...startStop} />}
                        <AddTimeButton color={object.color} {...addTime} />
                        <RenderBox {...screen.toCSS()} background={darken(.15, object.color)} borderRadius="4px" />
                        {/*
                        <RenderBox {...reset.toCSS()}>
                            <Button color={object.color} active={invoke === 'reset'} pressed={pressed}>
                                RESET
                            </Button>
                        </RenderBox>
                        <RenderBox {...inc.toCSS()}>
                            <Button color={object.color} active={invoke === 'inc'} pressed={pressed}>
                                <Icon src="deqo://icons/hero/plus" />
                            </Button>
                        </RenderBox> */}
                        {!beingEdited && <>
                            <RenderText
                                bounds={object.layout.minutes}
                                settings={StopwatchMinutesFieldTextSettings}
                                value={mins} />
                            <RenderText
                                bounds={object.layout.seconds}
                                settings={StopwatchSecondsFieldTextSettings}
                                value={secs} />
                        </>}
                        <RenderText
                            bounds={object.layout.colon}
                            settings={StopwatchFieldTextSettings}
                            value=":" />
                    </RenderBox>
                </RenderInner>
            </RenderInner>
        </RenderBounds>
    </>;
};

/* eslint-disable max-len */
/* eslint-disable no-mixed-operators */
// export class StopwatchVisualOld extends VisualBase<Stopwatch> {

//     @aspect()
//     private get shadow() {
//         // Compute shadow angle for rotation
//         const pt = new Point(0, 3).rotate(this.object.r * -1);
//         return `${pt.x}px ${pt.y}px 10px 0 rgba(0, 0, 0, 0.1)`;
//     }

//     @aspect()
//     private get borderStyling(): any {
//         const { object } = this;
//         return {
//             width: object.width,
//             height: object.height,
//             backgroundColor: object.color,
//             borderRadius: '6px',
//             boxShadow: this.shadow,
//         };
//     }

//     @aspect()
//     private get dropStyling(): any {
//         const { inner } = this.object.layout;
//         return {
//             left: inner.left,
//             top: inner.top,
//             width: inner.width,
//             height: inner.height,
//             borderRadius: '3px',
//         };
//     }

//     @aspect()
//     private get startButtonStyling(): any {
//         const area = this.object.layout.leftButton;
//         const pressed = this.isPressed;
//         const opacity = this.interaction?.hint === 'startStop' ? (pressed ? .3 : .2) : .1;
//         return {
//             left: area.left,
//             top: area.top,
//             width: area.width,
//             height: area.height,
//             background: `rgba(0, 0, 0, ${opacity})`,
//         };
//     }

//     @aspect()
//     private get resetButtonStyling(): any {
//         const area = this.object.layout.rightButton;
//         const pressed = this.isPressed;
//         const opacity = this.interaction?.hint === 'reset' ? (pressed ? .3 : .2) : .1;
//         return {
//             left: area.left,
//             top: area.top,
//             width: area.width,
//             height: area.height,
//             background: `rgba(0, 0, 0, ${opacity})`,
//         };
//     }

//     public render() {
//         const [mr, sr] = StopwatchReadingAttr.get(this.object) ?? [null, null];
//         return <RenderBounds object={this.object}>
//             <RenderBorder pose={this.pose} style={this.borderStyling}>
//                 <Button style={this.startButtonStyling}><div>{this.object.timeStarted ? 'Stop' : 'Start'}</div></Button>
//                 <Button style={this.resetButtonStyling}><div>
//                     +1m
//                 </div></Button>
//                 <FieldArea style={this.dropStyling}>:</FieldArea>
//                 <RenderGraffiti object={this.object} />
//                 {!this.isBeingEdited && <>
//                     <RenderTextField object={this.object} name="minutes" overrideValue={mr} />
//                     <RenderTextField object={this.object} name="seconds" overrideValue={sr} />
//                 </>}
//                 <RenderAnnotations object={this.object} />
//                 <RenderRankingMedal value={VoteRankAttr.get(this.object)} show={!this.isHighlighted} />
//             </RenderBorder>
//         </RenderBounds>;
//     }
// }

// const FieldArea = styled.div`
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     position: absolute;
//     background: rgba(0, 0, 0, .06);
//     font-size: 64px;
//     font-weight: 700;
//     font-family: 'Roboto Condensed';
//     color: #fff;
// `;

// const Button = styled.div`
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     position: absolute;
//     font-weight: 450;
//     font-family: 'Inter';
//     font-size: 14px;
//     line-height: 20px;
//     color: #fff;
//     border-radius: 6px;

//     & > div {
//         text-align: center;
//     }
// `;
